import { useMemo, useState } from 'react';
import { Button, Table, Dialog, ConfirmDialog, Input, Textarea, Select, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { PageHeader, QueryState, EmptyState } from '@/components';
import { errorText } from '@/lib/format';
import { useQuery } from '@/lib/useQuery';
import { useCan } from '@rbac/useCan';
import Can from '@rbac/Can';
import { useProductTypes } from '@/lib/productTypes';
import { useCrudList, useCrudWrite } from '../hooks';
import BrandSelect from '../components/BrandSelect';
import {
  LIMITS, addColumn, addRow, blankChart, chartDraft, chartPayload, problems, removeColumn, removeRow, renameColumn, setCell, sizedStoreTypes, sizedTypesText,
} from '../lib/sizeCharts';

/**
 * Size charts (MERCH-88), the Products "Size charts" tab: one chart per brand and product type, with
 * the store's own columns (Size, Chest cm, UK…) and rows. The storefront shows a listing's chart in
 * its buy box (storefront/components/SizeChart); storefront QA flags a fashion listing without one.
 * Server: models/SizeChart.model.js (catalog permissions, branch of its brand, audited).
 */
export default function SizeCharts({ embedded = false }) {
  const can = useCan();
  const { toast } = useToast();
  const registry = useProductTypes();
  const storeTypes = useQuery('storeProductTypes', {}, { select: (r) => (r && r.data) || [] });
  const list = useCrudList('SizeChart', { orderBy: { column: 'id', direction: 'asc' }, limit: 1000 });
  const brands = useCrudList('Brand', { select: ['id', 'name'], limit: 1000 });
  const write = useCrudWrite();
  const [draft, setDraft] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [formError, setFormError] = useState('');

  const types = sizedStoreTypes(registry.byKey, storeTypes.data);
  const brandName = useMemo(() => new Map((brands.data || []).map((b) => [String(b.id), b.name])), [brands.data]);
  const typeLabel = (k) => (registry.byKey[k] || {}).label || k;

  const save = async () => {
    setFormError('');
    try {
      if (draft.id) await write.update('SizeChart', draft.id, chartPayload(draft));
      else await write.create('SizeChart', chartPayload(draft));
      toast.success('Size chart saved', `${brandName.get(String(draft.brand_id)) || ''} ${typeLabel(draft.product_type)}`.trim());
      setDraft(null);
      list.reload();
    } catch (ex) {
      setFormError(errorText(ex));
    }
  };
  const remove = async () => {
    setFormError('');
    try {
      await write.run({ model: 'SizeChart', action: 'delete', where: { id: deleting.id } });
      toast.success('Size chart deleted');
      setDeleting(null);
      list.reload();
    } catch (ex) {
      setFormError(errorText(ex));
    }
  };
  const open = (d) => { setFormError(''); setDraft(d); };
  const actions = [
    can('catalog', 'update') && { name: 'edit', label: 'Edit', icon: <Pencil size={14} />, inline: true, onClick: ({ row }) => open(chartDraft(row)) },
    can('catalog', 'delete') && { name: 'delete', label: 'Delete', icon: <Trash2 size={14} />, onClick: ({ row }) => { setFormError(''); setDeleting(row); } },
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      <PageHeader title={embedded ? undefined : 'Size charts'} subtitle="One chart per brand and product type. Shoppers see it next to the size picker." />
      {types.length ? (
        <Can module="catalog" action="create">
          <Button leftIcon={<Plus size={16} />} onClick={() => open(blankChart(types[0].key))}>Add a size chart</Button>
        </Can>
      ) : (
        <Alert variant="info" title="No product types sold by size">Turn on {sizedTypesText(registry.types) || 'a type sold by size'} in your store settings to add size charts.</Alert>
      )}

      <QueryState query={list}>
        {(rows) => (rows.length ? (
          <Table
            data={rows}
            columns={[
              { key: 'brand', label: 'Brand', render: (r) => <span className="font-medium">{brandName.get(String(r.brand_id)) || '—'}</span> },
              { key: 'product_type', label: 'Type', render: (r) => typeLabel(r.product_type) },
              { key: 'name', label: 'Name', render: (r) => r.name || '—', hideOnMobile: true },
              { key: 'sizes', label: 'Sizes', render: (r) => chartDraft(r).rows.map((x) => x[0]).join(', ') },
            ]}
            actions={actions}
            replaceDefaultActions
            withAction={actions.length > 0}
            showSerial={false}
            showColumnPicker={false}
            pagination
            pageSize={25}
          />
        ) : (
          <EmptyState title="No size charts yet">Add one per brand and product type.</EmptyState>
        ))}
      </QueryState>

      <Dialog isOpen={!!draft} onClose={() => setDraft(null)} title={draft && draft.id ? 'Edit size chart' : 'Add a size chart'} size="xl">
        {draft ? <ChartEditor draft={draft} setDraft={setDraft} types={types} error={formError} saving={write.loading} onSave={save} onCancel={() => setDraft(null)} /> : null}
      </Dialog>

      <ConfirmDialog
        isOpen={!!deleting}
        title="Delete this size chart?"
        description="Shoppers stop seeing it on this brand's listings of this type."
        confirmLabel="Delete"
        loading={write.loading}
        onCancel={() => setDeleting(null)}
        onConfirm={remove}
      />
      {deleting && formError ? <Alert variant="error" title="Not deleted">{formError}</Alert> : null}
    </div>
  );
}

/** The chart grid: column names on top, one row of inputs per size. Only Save submits (every other button is type=button). */
export function ChartEditor({ draft, setDraft, types, error, saving, onSave, onCancel }) {
  const issues = problems(draft);
  const issueAt = (path) => (issues.find((p) => p.path === path) || {}).message;
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!issues.length) onSave(); }} data-testid="size-chart-editor">
      {draft.id ? null : (
        <div className="grid gap-3 sm:grid-cols-2">
          <BrandSelect value={draft.brand_id} typeKey={draft.product_type} required onChange={(id) => setDraft({ ...draft, brand_id: id })} error={issueAt('brand_id')} />
          <Select label="Product type" required options={types.map((t) => ({ value: t.key, label: t.label }))} value={draft.product_type} onChange={(v) => setDraft({ ...draft, product_type: v })} closeOnSelect />
        </div>
      )}
      <Input label="Name (optional)" value={draft.name} maxLength={LIMITS.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Men's shirts" />
      <div className="overflow-x-auto">
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${draft.columns.length}, minmax(7rem, 1fr)) auto` }}>
          {draft.columns.map((c, j) => (
            <div key={`c${j}`} className="flex items-end gap-1">
              <Input aria-label={`Column ${j + 1} name`} value={c} maxLength={LIMITS.cell} onChange={(e) => setDraft(renameColumn(draft, j, e.target.value))} error={issueAt(`columns.${j}`)} />
              {j > 0 ? <Button type="button" size="xs" variant="ghost" aria-label={`Remove column ${c || j + 1}`} onClick={() => setDraft(removeColumn(draft, j))}><X size={14} /></Button> : null}
            </div>
          ))}
          <span />
          {draft.rows.map((r, i) => [
            ...r.map((cell, j) => (
              <Input key={`r${i}c${j}`} aria-label={`Row ${i + 1}, ${draft.columns[j] || `column ${j + 1}`}`} value={cell} maxLength={LIMITS.cell} onChange={(e) => setDraft(setCell(draft, i, j, e.target.value))} error={issueAt(`rows.${i}.${j}`)} />
            )),
            <Button key={`r${i}x`} type="button" size="xs" variant="ghost" aria-label={`Remove row ${r[0] || i + 1}`} onClick={() => setDraft(removeRow(draft, i))}><X size={14} /></Button>,
          ])}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" leftIcon={<Plus size={14} />} onClick={() => setDraft(addRow(draft))} disabled={draft.rows.length >= LIMITS.rows}>Add a size</Button>
        <Button type="button" size="sm" variant="outline" leftIcon={<Plus size={14} />} onClick={() => setDraft(addColumn(draft))} disabled={draft.columns.length >= LIMITS.columns}>Add a column</Button>
      </div>
      <Textarea label="Note (optional)" value={draft.note} maxLength={LIMITS.note} rows={2} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="e.g. Measured flat; slim fit runs small." />
      {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={saving} disabled={issues.length > 0}>Save</Button>
      </div>
    </form>
  );
}
